/**
 * AP-10 — FM-WIN-11: Engine Simulator.
 *
 * Signalformeln, Wellenparameter, PGNs und Byte-Layouts sind 1:1 aus
 * EngineSimulatorWindow.cpp uebernommen (Fundstellen an den jeweiligen
 * Konstanten). Der Takt liegt hier im Haupt-Thread wie im Original im
 * Fenstertimer; gesendet wird ueber `connection.sendFrame()`, im
 * Simulationsmodus schleift der Worker den Frame in den Empfangspfad zurueck.
 *
 * Die Simulation laeuft weiter, waehrend das Fenster verborgen ist
 * (EngineSimulatorWindow.h:25-40).
 */

import './viewsD.css';
import { createFloatingPanel } from './filters/FloatingPanel';
import { createButton, setPlayStopState } from '../ui/controls';
import type { AppStore } from '../app/store';
import type { CanConnection } from '../app/connection';

/** `kTickIntervalMs` (EngineSimulatorWindow.h:104). */
const TICK_MS = 50;
/** Langsame Zeilen senden jeden 20. Tick, also ~1 s (EngineSimulatorWindow.cpp:202). */
const SLOW_EVERY = 20;
/** `kEngineSa` — Source Address 0 = Engine #1. */
const ENGINE_SA = 0;

/** `BuildJ1939Id`: `(priority << 26) | (pgn << 8) | sa`. */
function j1939Id(priority: number, pgn: number, sa: number): number {
  return ((priority << 26) | (pgn << 8) | sa) >>> 0;
}

const ID_EEC1 = j1939Id(3, 0xf004, ENGINE_SA);
const ID_EEC2 = j1939Id(3, 0xf003, ENGINE_SA);
const ID_ET1 = j1939Id(6, 0xfeee, ENGINE_SA);
const ID_EFL_P1 = j1939Id(6, 0xfeef, ENGINE_SA);
const ID_LFE = j1939Id(6, 0xfef2, ENGINE_SA);
const ID_VEP1 = j1939Id(6, 0xfef7, ENGINE_SA);
const ID_HOURS = j1939Id(6, 0xfee5, ENGINE_SA);
/** Zusatzrahmen, feste IDs (EngineSimulatorWindow.cpp:44-50). */
const ID_EXTRA = [0x0c00fefe, 0x18fdc0fe, 0x18fdbbfe, 0x18fdbcfe, 0x18fdbdfe, 0x18fdbefe, 0x18fe56fe];

type WaveShape = 0 | 1 | 2 | 3 | 4;
export interface Wave {
  shape: WaveShape;
  periodS: number;
  phaseS: number;
}

function makeWave(minPeriodS: number, maxPeriodS: number): Wave {
  const periodS = minPeriodS + Math.random() * (maxPeriodS - minPeriodS);
  return {
    shape: Math.floor(Math.random() * 5) as WaveShape,
    periodS,
    phaseS: Math.random() * periodS,
  };
}

/** `SampleWave` (EngineSimulatorWindow.cpp:439-462). */
export function sampleWave(wave: Wave, timeS: number, offset: number, amplitude: number): number {
  const phase = ((timeS + wave.phaseS) % wave.periodS) / wave.periodS;
  let shaped: number;
  switch (wave.shape) {
    case 0: shaped = Math.sin(phase * 2 * Math.PI); break;
    case 1: shaped = 2 * phase - 1; break;
    case 2: shaped = phase < 0.5 ? 4 * phase - 1 : 3 - 4 * phase; break;
    case 3: shaped = phase < 0.5 ? 1 : -1; break;
    // Zigzag: schneller Anstieg auf den ersten 20 %, langsamer Abfall danach.
    default: shaped = phase < 0.2 ? (phase / 0.2) * 2 - 1 : 1 - 2 * ((phase - 0.2) / 0.8); break;
  }
  return offset + amplitude * shaped;
}

function jitter(amplitude: number): number {
  return (Math.random() - 0.5) * 2 * amplitude;
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

function u8(value: number): number {
  return clamp(Math.trunc(value), 0, 255) & 0xff;
}

/** Little-endian 16 Bit an `offset` schreiben. */
function put16(d: number[], offset: number, value: number): void {
  const v = clamp(Math.round(value), 0, 0xffff);
  d[offset] = v & 0xff;
  d[offset + 1] = (v >>> 8) & 0xff;
}

function put32(d: number[], offset: number, value: number): void {
  const v = clamp(Math.round(value), 0, 0xffffffff);
  d[offset] = v & 0xff;
  d[offset + 1] = (v >>> 8) & 0xff;
  d[offset + 2] = (v >>> 16) & 0xff;
  d[offset + 3] = (v >>> 24) & 0xff;
}

interface SimRow {
  label: string;
  fast: boolean;
  active: boolean;
  build: () => { id: number; data: number[] };
  button: HTMLButtonElement;
}

export interface EngineSimulatorHandle {
  show(): void;
}

export function createEngineSimulator(
  store: AppStore,
  connection: CanConnection,
): EngineSimulatorHandle {
  const panel = createFloatingPanel('Engine Simulator');
  panel.element.classList.add('cm-panel-window--medium');

  // Jede Groesse hat ihre eigene Kurve mit eigener Periode und Phase, damit
  // sich die Verlaeufe nicht synchron bewegen (EngineSimulatorWindow.cpp:391).
  const waves = {
    rpm: makeWave(10, 30),
    pedal: makeWave(8, 25),
    load: makeWave(8, 25),
    coolant: makeWave(15, 40),
    oil: makeWave(10, 30),
    fuel: makeWave(8, 25),
    volt: makeWave(10, 30),
    torqueMode: makeWave(6, 15),
    demandTorque: makeWave(8, 25),
    starterMode: makeWave(20, 40),
    eec2Switch: makeWave(5, 12),
    fuelTemp: makeWave(15, 40),
    oilTemp: makeWave(15, 40),
    turboOilTemp: makeWave(15, 40),
    intercoolerTemp: makeWave(15, 40),
    intercoolerThermostat: makeWave(10, 30),
    fuelDeliveryPressure: makeWave(10, 30),
    crankcaseBlowBy: makeWave(10, 30),
    oilLevel: makeWave(20, 45),
    crankcasePressure: makeWave(10, 30),
    coolantPressure: makeWave(10, 30),
    coolantLevel: makeWave(25, 45),
    fuelEconomyInst: makeWave(8, 25),
    fuelEconomyAvg: makeWave(15, 35),
    throttlePosition: makeWave(8, 25),
    netBatteryCurrent: makeWave(8, 25),
    alternatorCurrent: makeWave(8, 25),
  };
  const extraWaves = Array.from({ length: 7 * 8 }, () => makeWave(5, 20));

  let simTimeS = 0;
  let tickCount = 0;
  const v = {
    rpm: 0, pedal: 0, load: 0, coolant: 0, oilPressure: 0, fuelRate: 0, battery: 0,
    demandTorque: 0, fuelTemp: 0, oilTemp: 0, turboOilTemp: 0, intercoolerTemp: 0,
    intercoolerThermostat: 0, fuelDeliveryPressure: 0, crankcaseBlowBy: 0, oilLevel: 0,
    crankcasePressure: 0, coolantPressure: 0, coolantLevel: 0, fuelEconomyInst: 0,
    fuelEconomyAvg: 0, throttlePosition: 0, netBatteryCurrent: 0, alternatorCurrent: 0,
    torqueModeState: 0, starterModeState: 0, eec2Switch: 0,
  };

  /** Diskreter Zustand aus einer Welle: `SampleWave(w,0,1)` auf 0..n-1 abgebildet. */
  function stateFrom(wave: Wave, stateCount: number): number {
    const normalized = (sampleWave(wave, simTimeS, 0, 1) + 1) / 2;
    return clamp(Math.floor(normalized * stateCount), 0, stateCount - 1);
  }

  function updateValues(): void {
    v.rpm = Math.max(0, sampleWave(waves.rpm, simTimeS, 1600, 900) + jitter(4));
    v.pedal = clamp(sampleWave(waves.pedal, simTimeS, 40, 40), 0, 100);
    v.load = clamp(sampleWave(waves.load, simTimeS, 50, 50), 0, 100);
    v.coolant = clamp(sampleWave(waves.coolant, simTimeS, 55, 35) + jitter(0.15), -40, 125);
    v.oilPressure = Math.max(0, sampleWave(waves.oil, simTimeS, 300, 150) + jitter(5));
    v.fuelRate = Math.max(0, sampleWave(waves.fuel, simTimeS, 8, 7) + jitter(0.3));
    v.battery = sampleWave(waves.volt, simTimeS, 13.3, 0.7) + jitter(0.03);
    v.demandTorque = clamp(sampleWave(waves.demandTorque, simTimeS, 40, 40), 0, 100);
    v.fuelTemp = clamp(sampleWave(waves.fuelTemp, simTimeS, 40, 30) + jitter(0.2), -40, 210);
    v.oilTemp = clamp(sampleWave(waves.oilTemp, simTimeS, 90, 20) + jitter(0.2), -273, 1735);
    v.turboOilTemp = clamp(sampleWave(waves.turboOilTemp, simTimeS, 90, 25) + jitter(0.2), -273, 1735);
    v.intercoolerTemp = clamp(sampleWave(waves.intercoolerTemp, simTimeS, 50, 30) + jitter(0.15), -40, 210);
    v.intercoolerThermostat = clamp(sampleWave(waves.intercoolerThermostat, simTimeS, 50, 50), 0, 100);
    v.fuelDeliveryPressure = Math.max(0, sampleWave(waves.fuelDeliveryPressure, simTimeS, 400, 150) + jitter(5));
    v.crankcaseBlowBy = sampleWave(waves.crankcaseBlowBy, simTimeS, 1.5, 1.5) + jitter(0.1);
    v.oilLevel = clamp(sampleWave(waves.oilLevel, simTimeS, 70, 20), 0, 100);
    v.crankcasePressure = sampleWave(waves.crankcasePressure, simTimeS, 0, 2) + jitter(0.1);
    v.coolantPressure = Math.max(0, sampleWave(waves.coolantPressure, simTimeS, 150, 60) + jitter(3));
    v.coolantLevel = clamp(sampleWave(waves.coolantLevel, simTimeS, 85, 15), 0, 100);
    v.fuelEconomyInst = Math.max(0, sampleWave(waves.fuelEconomyInst, simTimeS, 10, 8) + jitter(0.3));
    v.fuelEconomyAvg = Math.max(0, sampleWave(waves.fuelEconomyAvg, simTimeS, 10, 4) + jitter(0.1));
    v.throttlePosition = clamp(sampleWave(waves.throttlePosition, simTimeS, 40, 40), 0, 100);
    v.netBatteryCurrent = sampleWave(waves.netBatteryCurrent, simTimeS, 0, 60) + jitter(2);
    v.alternatorCurrent = Math.max(0, sampleWave(waves.alternatorCurrent, simTimeS, 40, 40) + jitter(2));
    v.torqueModeState = stateFrom(waves.torqueMode, 16);
    v.starterModeState = stateFrom(waves.starterMode, 4);
    v.eec2Switch = stateFrom(waves.eec2Switch, 4);
  }

  function extraFrame(index: number): { id: number; data: number[] } {
    const data = Array.from({ length: 8 }, (_, i) =>
      u8(Math.round(clamp(sampleWave(extraWaves[index * 8 + i] as Wave, simTimeS, 128, 127), 0, 255))),
    );
    return { id: ID_EXTRA[index] as number, data };
  }

  const rows: SimRow[] = [];
  const list = document.createElement('div');
  list.className = 'cm-simlist';

  function addRow(label: string, fast: boolean, build: () => { id: number; data: number[] }): void {
    const button = createButton('\u25b6', {
      onClick: () => {
        const row = rows.find((r) => r.button === button);
        if (row === undefined) return;
        row.active = !row.active;
        setPlayStopState(button, row.active);
        updateActivateAllLabel();
      },
    });
    setPlayStopState(button, false);
    const element = document.createElement('div');
    element.className = 'cm-simrow';
    const name = document.createElement('span');
    name.textContent = label;
    element.append(button, name);
    list.append(element);
    rows.push({ label, fast, active: false, build, button });
  }

  addRow('EEC1 (Engine Speed)', true, () => {
    const d = new Array<number>(8).fill(0);
    d[0] = 0xf0 | (v.torqueModeState & 0x0f);
    d[1] = u8(clamp(v.load + 125, 0, 250));
    d[2] = u8(clamp(v.load + 125, 0, 250));
    put16(d, 3, v.rpm / 0.125); // SPN190, 0.125 rpm/bit
    d[5] = 0xff;
    d[6] = 0xfc | (v.starterModeState & 0x03);
    d[7] = u8(clamp(v.demandTorque + 125, 0, 250)); // SPN2432
    return { id: ID_EEC1, data: d };
  });
  addRow('EEC2 (Pedal / Load)', true, () => {
    const d = new Array<number>(8).fill(0xff);
    d[0] = 0xf0 | (v.eec2Switch & 0x0f);
    d[1] = u8(clamp(v.pedal / 0.4, 0, 250)); // SPN91, 0.4 %/bit
    d[2] = u8(clamp(v.load, 0, 250)); // SPN92, 1 %/bit
    d[3] = u8(clamp(v.pedal / 0.4, 0, 250));
    return { id: ID_EEC2, data: d };
  });
  addRow('ET1 (Coolant Temp)', false, () => {
    const d = new Array<number>(8).fill(0);
    d[0] = u8(clamp(v.coolant + 40, 0, 250)); // SPN110, 1 C/bit, -40
    d[1] = u8(clamp(v.fuelTemp + 40, 0, 250)); // SPN174
    put16(d, 2, (v.oilTemp + 273) / 0.03125); // SPN175
    put16(d, 4, (v.turboOilTemp + 273) / 0.03125);
    d[6] = u8(clamp(v.intercoolerTemp + 40, 0, 250)); // SPN52
    d[7] = u8(clamp(v.intercoolerThermostat / 0.4, 0, 250)); // SPN962
    return { id: ID_ET1, data: d };
  });
  addRow('EFL/P1 (Oil Pressure)', false, () => {
    const d = new Array<number>(8).fill(0);
    d[0] = u8(clamp(v.fuelDeliveryPressure / 4, 0, 250)); // SPN94, 4 kPa/bit
    d[1] = u8(clamp(v.crankcaseBlowBy / 0.05, 0, 250)); // SPN95
    d[2] = u8(clamp(v.oilLevel / 0.4, 0, 250)); // SPN98
    d[3] = u8(clamp(v.oilPressure / 4, 0, 250)); // SPN100
    put16(d, 4, (v.crankcasePressure + 250) / 0.0078125); // SPN101
    d[6] = u8(clamp(v.coolantPressure / 2, 0, 250)); // SPN109
    d[7] = u8(clamp(v.coolantLevel / 0.4, 0, 250)); // SPN111
    return { id: ID_EFL_P1, data: d };
  });
  addRow('LFE (Fuel Rate)', false, () => {
    const d = new Array<number>(8).fill(0);
    put16(d, 0, v.fuelRate / 0.05); // SPN183
    put16(d, 2, v.fuelEconomyInst * 512); // SPN184, 1/512 per bit
    put16(d, 4, v.fuelEconomyAvg * 512); // SPN185
    d[6] = u8(clamp(v.throttlePosition / 0.4, 0, 250)); // SPN51
    d[7] = 0xff;
    return { id: ID_LFE, data: d };
  });
  addRow('VEP1 (Battery Voltage)', false, () => {
    const d = new Array<number>(8).fill(0xff);
    put16(d, 0, clamp(v.netBatteryCurrent + 125, 0, 250)); // SPN114, -125 offset
    d[2] = u8(clamp(v.alternatorCurrent, 0, 250)); // SPN115
    put16(d, 3, v.battery / 0.05); // SPN168, 0.05 V/bit
    return { id: ID_VEP1, data: d };
  });
  addRow('Engine Hours', false, () => {
    const d = new Array<number>(8).fill(0);
    put32(d, 0, (simTimeS / 3600) / 0.05); // SPN247, 0.05 h/bit
    put32(d, 4, (v.rpm * simTimeS) / 60 / 1000); // SPN249, 1000 rev/bit
    return { id: ID_HOURS, data: d };
  });
  const EXTRA_LABELS = [
    'Extra: PGN 0x0000 (TSC1)',
    'Extra: PGN 0xFDC0',
    'Extra: PGN 0xFDBB',
    'Extra: PGN 0xFDBC',
    'Extra: PGN 0xFDBD',
    'Extra: PGN 0xFDBE',
    'Extra: PGN 0xFE56',
  ];
  EXTRA_LABELS.forEach((label, index) => addRow(label, false, () => extraFrame(index)));

  const readout = document.createElement('pre');
  readout.className = 'cm-sim__readout';

  const activateAll = createButton('Activate All', {
    onClick: () => {
      const target = !rows.every((row) => row.active);
      for (const row of rows) {
        row.active = target;
        setPlayStopState(row.button, target);
      }
      updateActivateAllLabel();
    },
  });

  function updateActivateAllLabel(): void {
    activateAll.textContent = rows.every((row) => row.active) ? 'Deactivate All' : 'Activate All';
  }

  function pad(value: number, digits: number): string {
    return value.toFixed(digits).padStart(7, ' ');
  }

  function updateReadout(): void {
    readout.textContent =
      `Engine Speed: ${pad(v.rpm, 0)} rpm\n` +
      `Pedal:        ${pad(v.pedal, 1)} %\n` +
      `Load:         ${pad(v.load, 1)} %\n` +
      `Coolant Temp: ${pad(v.coolant, 1)} C\n` +
      `Oil Pressure: ${pad(v.oilPressure, 0)} kPa\n` +
      `Fuel Rate:    ${pad(v.fuelRate, 1)} L/h\n` +
      `Battery:      ${pad(v.battery, 2)} V\n` +
      `Engine Hours: ${pad(simTimeS / 3600, 2)} h`;
  }

  const layout = document.createElement('div');
  layout.className = 'cm-view';
  const toolbar = document.createElement('div');
  toolbar.className = 'cm-view__row';
  toolbar.append(activateAll);
  layout.append(toolbar, list, readout);
  panel.body.append(layout);

  function tick(): void {
    // Ohne aktive Zeile und ohne sichtbares Fenster gibt es nichts zu rechnen:
    // die 27 Wellenformen alle 50 ms auszuwerten waere reine Leerlauflast.
    const anyActive = rows.some((row) => row.active);
    if (!anyActive && !panel.visible) return;

    simTimeS += TICK_MS / 1000;
    tickCount += 1;
    updateValues();
    const sendSlow = tickCount % SLOW_EVERY === 0;
    for (const row of rows) {
      if (!row.active) continue;
      if (!row.fast && !sendSlow) continue;
      const frame = row.build();
      void connection
        .sendFrame({ id: frame.id, data: frame.data, dlc: 8, extended: true })
        .catch(() => {
          // Ohne Adapter und ohne Simulationsmodus gibt es kein Ziel; das
          // Original meldet den Fehler ebenfalls nur einmalig im Status.
          store.emit('status:changed', { text: 'Send blocked: connect first' });
          for (const r of rows) {
            r.active = false;
            setPlayStopState(r.button, false);
          }
          updateActivateAllLabel();
        });
    }
    if (panel.visible) updateReadout();
  }

  // Laeuft unabhaengig von der Sichtbarkeit weiter.
  setInterval(tick, TICK_MS);
  updateValues();
  updateReadout();

  return { show: () => panel.show() };
}
